import { cn } from '../lib/cn';
import { Icon } from './icon';

export interface PullIndicatorProps {
  /** How far the indicator has come down, in pixels. 0 hides it. */
  distance: number;
  /** From this distance on, letting go refreshes. */
  threshold: number;
  refreshing?: boolean;
}

/**
 * Indicator of pull to refresh under the top bar (docs/ui/responsive.md, Gesten). The arrow turns
 * with the pull and takes the primary color once letting go refreshes; while refreshing it spins.
 * Purely visual: whoever refreshes announces the result for screen readers.
 */
export function PullIndicator({ distance, threshold, refreshing = false }: PullIndicatorProps) {
  const offset = refreshing ? threshold : distance;
  const progress = Math.min(offset / threshold, 1);
  const armed = refreshing || progress >= 1;
  // Follows the finger without delay; springs back and settles with a transition.
  const following = distance > 0 && !refreshing;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-(--topbar-h) z-30 flex justify-center"
    >
      <span
        className={cn(
          'flex size-10 items-center justify-center rounded-full border border-line bg-surface shadow-overlay',
          armed ? 'text-brand' : 'text-fg-muted',
          !following && 'transition-[translate,opacity] transition-base',
        )}
        style={{
          translate: `0 calc(${offset}px - 100%)`,
          opacity: offset > 0 ? Math.max(progress, 0.3) : 0,
        }}
      >
        <span
          className={cn('flex', refreshing && 'animate-spin motion-reduce:animate-none')}
          style={refreshing ? undefined : { rotate: `${progress * 270}deg` }}
        >
          <Icon name="refresh-cw" className="size-5" />
        </span>
      </span>
    </div>
  );
}
